import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Save } from "@mui/icons-material";
import { usePMVV, useUpdatePMVV } from "@/hooks/usePMVV";
import type { PmvvValue } from "@/types";

export default function PMVVEditor() {
  const { data, isLoading } = usePMVV();
  const updateMutation = useUpdatePMVV();

  const [purpose, setPurpose] = useState("");
  const [mission, setMission] = useState("");
  const [vision, setVision] = useState("");
  const [values, setValues] = useState<PmvvValue[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (data) {
      setPurpose(data.purpose);
      setMission(data.mission);
      setVision(data.vision);
      setValues(data.values);
    }
  }, [data]);

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        purpose,
        mission,
        vision,
        values: values.filter((v) => v.title.trim() !== ""),
      });
      setSnackbar({
        open: true,
        message: "PMVV saved — KB article regenerating",
        severity: "success",
      });
    } catch {
      setSnackbar({ open: true, message: "Failed to save", severity: "error" });
    }
  };

  const addValue = () => setValues((v) => [...v, { title: "", body: "" }]);
  const removeValue = (idx: number) => setValues((v) => v.filter((_, i) => i !== idx));
  const updateValue = (idx: number, field: keyof PmvvValue, val: string) =>
    setValues((vs) => vs.map((v, i) => (i === idx ? { ...v, [field]: val } : v)));

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Purpose, Mission, Vision, Values
        </Typography>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? "Saving…" : "Save"}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Edit the strategic anchors here. Saving regenerates the canonical KB article in the
        Purpose/Mission/Vision/Values category, which AI-00 reads on every heartbeat and which
        drives PMVV alignment scoring on new KB articles.
        {data?.updated_at && (
          <> Last updated {new Date(data.updated_at).toLocaleString("en-GB")}.</>
        )}
      </Typography>

      <Stack spacing={2}>
        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h6">Purpose</Typography>
            <Typography variant="caption" color="text.secondary">
              Why we exist. The reason the business is here.
            </Typography>
            <TextField
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder="e.g. To help leaders make sharper, faster, more confident decisions."
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h6">Mission</Typography>
            <Typography variant="caption" color="text.secondary">
              What we do, for whom, how. The work itself.
            </Typography>
            <TextField
              value={mission}
              onChange={(e) => setMission(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder="e.g. We design and run strategic-execution programmes for ambitious mid-market leaders."
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h6">Vision</Typography>
            <Typography variant="caption" color="text.secondary">
              The future state we are working towards. Aspirational but specific.
            </Typography>
            <TextField
              value={vision}
              onChange={(e) => setVision(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder="e.g. By 2030, Elite Mind is the trusted strategic partner for 100+ UK consultancies."
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Values</Typography>
                <Typography variant="caption" color="text.secondary">
                  Short title + a paragraph on what each one means in practice.
                </Typography>
              </Box>
              <Button startIcon={<Add />} size="small" onClick={addValue}>
                Add value
              </Button>
            </Box>

            {values.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                No values defined yet. Click "Add value" to start.
              </Typography>
            )}

            <Stack spacing={2}>
              {values.map((v, idx) => (
                <Card variant="outlined" key={idx}>
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <TextField
                        label={`Value ${idx + 1} — title`}
                        value={v.title}
                        onChange={(e) => updateValue(idx, "title", e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <IconButton
                        onClick={() => removeValue(idx)}
                        size="small"
                        title="Remove value"
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      label="What it means in practice"
                      value={v.body}
                      onChange={(e) => updateValue(idx, "body", e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                    />
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
